import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, orderBy, query, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../shared/lib/firebase';
import { useAuth } from '../auth/useAuth';

export type Announcement = {
  id: string;
  title: string;
  body: string;
  urgent: boolean;
  publishedAt: string;
};

/** `uidOverride` existe só para teste (hook sem AuthProvider). */
export function useAnnouncements(uidOverride?: string) {
  const { currentUser } = useAuth();
  const uid = uidOverride ?? currentUser?.uid;
  const [items, setItems] = useState<Announcement[]>([]);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!uid) return;
    const q = query(collection(db, 'system_announcements'), orderBy('publishedAt', 'desc'));
    return onSnapshot(
      q,
      (snap) => setItems(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Announcement, 'id'>) }))),
      (err) => console.error('[useAnnouncements]', err),
    );
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    return onSnapshot(
      collection(db, 'users', uid, 'announcement_reads'),
      (snap) => setReadIds(new Set(snap.docs.map((d) => d.id))),
      (err) => console.error('[useAnnouncements] reads', err),
    );
  }, [uid]);

  const unread = useMemo(() => items.filter((a) => !readIds.has(a.id)), [items, readIds]);
  // items vem do mais novo para o mais antigo: o último urgente sem read é o mais antigo.
  const pendingUrgent = useMemo(() => unread.filter((a) => a.urgent).at(-1) ?? null, [unread]);

  const markRead = useCallback(
    async (ids: string[]) => {
      if (!uid) return;
      const faltam = ids.filter((id) => !readIds.has(id));
      await Promise.all(
        faltam.map((id) =>
          setDoc(doc(db, 'users', uid, 'announcement_reads', id), { at: serverTimestamp() }).catch((e: { code?: string }) => {
            // create de read que já existe é negado pela regra (sem update): ignorar.
            if (e?.code !== 'permission-denied') throw e;
          }),
        ),
      );
    },
    [uid, readIds],
  );

  const acknowledge = useCallback((id: string) => markRead([id]), [markRead]);

  return { items, unread, pendingUrgent, markRead, acknowledge };
}
